// Lightweight client-side validators used by the contact / appointment forms.
// Each validator returns an i18n key when invalid (so callers can localise the
// message via t(key, fallback)) or null when the value is acceptable.

export const required = (v) =>
  String(v ?? '').trim().length > 0 ? null : 'validation.required';

export const minLength = (n) => (v) =>
  String(v ?? '').trim().length >= n ? null : `validation.min:${n}`;

export const maxLength = (n) => (v) =>
  String(v ?? '').trim().length <= n ? null : `validation.max:${n}`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const email = (v) => {
  const val = String(v ?? '').trim();
  if (!val) return null; // Combine with required() if the field is mandatory
  return EMAIL_RE.test(val) ? null : 'validation.email';
};

const PHONE_RE = /^[\d\s\-+()]{6,20}$/;
export const phone = (v) => {
  const val = String(v ?? '').trim();
  if (!val) return null;
  return PHONE_RE.test(val) ? null : 'validation.phone';
};

// Run a chain of validators, returning the first failing key (or null).
export const compose = (...fns) => (v) => {
  for (const fn of fns) {
    const err = fn(v);
    if (err) return err;
  }
  return null;
};

// Run a `{ field: validator }` map and return `{ field: errorKey }` for fields
// that failed (and only those). The result is empty when the form is valid.
export const validateForm = (values, schema) => {
  const errors = {};
  for (const [field, validator] of Object.entries(schema)) {
    const err = validator(values[field]);
    if (err) errors[field] = err;
  }
  return errors;
};

// Decode a key like `validation.min:10` into a localised, human-readable
// message. Falls back to a sensible English message when no translation is
// found.
export const formatError = (errorKey, t, fallbacks = {}) => {
  if (!errorKey) return '';
  const [base, arg] = errorKey.split(':');
  const fallback = fallbacks[base] || DEFAULT_FALLBACKS[base] || 'Invalid value';
  if (arg) {
    return t(base, fallback).replace('{n}', arg);
  }
  return t(base, fallback);
};

const DEFAULT_FALLBACKS = {
  'validation.required': 'This field is required.',
  'validation.email': 'Please provide a valid email address.',
  'validation.phone': 'Please provide a valid phone number.',
  'validation.min': 'Must be at least {n} characters.',
  'validation.max': 'Must be {n} characters or less.',
  'validation.consent': 'Please accept to continue.',
  'validation.generic': 'Please check this field.',
};

// Map API error responses (`{ errors: [{ field, message }] }`) onto the same
// error shape the form uses. We keep the API-supplied English message verbatim
// so the visitor still sees the exact backend reason — that's better than
// hiding a real validation error behind a generic toast.
export const errorsFromApi = (payload) => {
  const list = Array.isArray(payload?.errors) ? payload.errors : [];
  if (!list.length) return null;
  const out = {};
  for (const err of list) {
    if (err?.field && err?.message) {
      out[err.field] = err.message;
    }
  }
  return Object.keys(out).length ? out : null;
};
